import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getCurrentStore,
  updateCurrentStore,
  type StoreSettingsUpdate,
} from '@/lib/api/stores.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { calculateCashDiscount } from '@/lib/cash-discount.js';
import { formatCurrency } from '@/lib/format.js';
import {
  depositModeValue,
  numericValue,
  storeField,
  toBool,
} from '@/pages/settings/settings-utils.js';

export function PaymentsSettingsPage() {
  const queryClient = useQueryClient();
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const store = storeQuery.data;
  const [form, setForm] = useState<Record<string, string | boolean>>({});

  const value = (key: Parameters<typeof storeField>[1], fallback = '') => {
    const formValue = form[key];
    return typeof formValue === 'boolean'
      ? String(formValue)
      : (formValue ?? storeField(store, key, fallback));
  };
  const boolValue = (key: Parameters<typeof storeField>[1], fallback = false) =>
    typeof form[key] === 'boolean' ? Boolean(form[key]) : toBool(store?.[key], fallback);

  const taxRatePercentDisplay = () => {
    if (typeof form.taxRate === 'string') return form.taxRate;
    const stored = Number(store?.taxRate ?? 0);
    return String(Number((stored * 100).toFixed(4)));
  };

  const cashDiscountEnabled = boolValue('cashDiscountEnabled', false);
  const cashDiscountRatePct = () => {
    if (typeof form.cashDiscountRate === 'string') return form.cashDiscountRate;
    const stored = Number(store?.cashDiscountRate ?? 0.035);
    return String(Number((stored * 100).toFixed(4)));
  };
  const cashDiscountMode =
    (typeof form.cashDiscountMode === 'string'
      ? form.cashDiscountMode
      : store?.cashDiscountMode) === 'cash_discount'
      ? 'cash_discount'
      : 'card_surcharge';
  const cashDiscountLabel =
    (typeof form.cashDiscountLabel === 'string'
      ? form.cashDiscountLabel
      : store?.cashDiscountLabel) || 'Cash Discount';

  const preview = useMemo(() => {
    const rate = numericValue(cashDiscountRatePct(), 3.5) / 100;
    return calculateCashDiscount(249.99, {
      cashDiscountEnabled: true,
      cashDiscountRate: rate,
      cashDiscountMode,
      cashDiscountLabel,
    });
  }, [cashDiscountLabel, cashDiscountMode, form.cashDiscountRate, store?.cashDiscountRate]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: StoreSettingsUpdate = {
        taxMode: (value('taxMode', 'parts_only') as 'parts_only' | 'all' | 'none') || 'parts_only',
        taxRate: numericValue(taxRatePercentDisplay(), 0) / 100,
        showPartsOnReceipt: boolValue('showPartsOnReceipt', false),
        trackSerialParts: boolValue('trackSerialParts', false),
        depositMode: depositModeValue(value('depositMode', 'optional')),
        depositRequiredAmount: numericValue(value('depositRequiredAmount', '0'), 0),
        depositRequiredPct: numericValue(value('depositRequiredPct', '0'), 0),
        depositRulePartsOrder: boolValue('depositRulePartsOrder', false),
        depositRuleMinAmount: numericValue(value('depositRuleMinAmount', '0'), 0),
        depositRefundable: boolValue('depositRefundable', true),
        cashDiscountEnabled,
        cashDiscountRate: numericValue(cashDiscountRatePct(), 3.5) / 100,
        cashDiscountMode,
        cashDiscountLabel: String(cashDiscountLabel).trim() || 'Cash Discount',
        cashDiscountShowOnReceipt: boolValue('cashDiscountShowOnReceipt', true),
        terminalAppliesSurcharge: boolValue('terminalAppliesSurcharge', true),
        tipsEnabled: boolValue('tipsEnabled', false),
        tipsOnCard: boolValue('tipsOnCard', true),
        tipsOnCash: boolValue('tipsOnCash', false),
        tipsRequirePin: boolValue('tipsRequirePin', true),
        tipAttribution: (value('tipAttribution', 'checkout_staff') ||
          'checkout_staff') as StoreSettingsUpdate['tipAttribution'],
        tipSplitConfig: {
          checkout: numericValue(value('tipSplitCheckout', '50'), 50),
          tech: numericValue(value('tipSplitTech', '50'), 50),
        },
        dailyRevenueGoal: value('dailyRevenueGoal') ? Number(value('dailyRevenueGoal')) : null,
        weeklyRevenueGoal: value('weeklyRevenueGoal') ? Number(value('weeklyRevenueGoal')) : null,
        monthlyRevenueGoal: value('monthlyRevenueGoal')
          ? Number(value('monthlyRevenueGoal'))
          : null,
        avgLaborRateGoal: value('avgLaborRateGoal') ? Number(value('avgLaborRateGoal')) : null,
      };
      return updateCurrentStore(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Payment settings saved');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const depositMode = value('depositMode', 'optional');

  if (storeQuery.isLoading) {
    return <div className="rounded-2xl bg-white p-6 text-slate-500">Loading…</div>;
  }

  return (
    <form
      className="space-y-6"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Tax Settings</h2>
        <fieldset className="mt-4 space-y-2">
          <legend className="text-sm font-medium text-slate-700">Tax Mode</legend>
          {(
            [
              ['parts_only', 'Parts Only (Michigan — tax on parts)'],
              ['all', 'Parts & Services (tax everything)'],
              ['none', 'No Tax'],
            ] as const
          ).map(([mode, label]) => (
            <label key={mode} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="taxMode"
                checked={
                  (form.taxMode as string) === mode ||
                  (!form.taxMode && (store?.taxMode ?? 'parts_only') === mode)
                }
                onChange={() => setForm((c) => ({ ...c, taxMode: mode }))}
              />
              {label}
            </label>
          ))}
        </fieldset>
        <label className="mt-4 block max-w-xs">
          <span className="text-sm font-medium text-slate-700">Tax Rate (%)</span>
          <input
            type="number"
            min={0}
            max={100}
            step="0.01"
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={taxRatePercentDisplay()}
            onChange={(e) => setForm((c) => ({ ...c, taxRate: e.target.value }))}
            placeholder="6"
          />
          <span className="mt-1 block text-xs text-slate-500">
            Enter a whole number, e.g. 6 for 6%.
          </span>
        </label>
        <label className="mt-4 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
          <span>
            Show parts on receipts
            <span className="mt-0.5 block text-xs font-normal text-slate-500">
              When off, tax shows as &quot;Tax included&quot;
            </span>
          </span>
          <input
            type="checkbox"
            checked={boolValue('showPartsOnReceipt', false)}
            onChange={(e) => setForm((c) => ({ ...c, showPartsOnReceipt: e.target.checked }))}
          />
        </label>
        <label className="mt-2 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
          <span>Track serialized parts</span>
          <input
            type="checkbox"
            checked={boolValue('trackSerialParts', false)}
            onChange={(e) => setForm((c) => ({ ...c, trackSerialParts: e.target.checked }))}
          />
        </label>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Deposit Settings</h2>
        <select
          className="mt-4 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={depositMode}
          onChange={(e) => setForm((c) => ({ ...c, depositMode: e.target.value }))}
        >
          <option value="none">None</option>
          <option value="optional">Optional</option>
          <option value="required">Required</option>
          <option value="rule_based">Rule-Based</option>
        </select>
        {(depositMode === 'required' || depositMode === 'rule_based') && (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium">Fixed amount</span>
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={value('depositRequiredAmount', '0')}
                onChange={(e) => setForm((c) => ({ ...c, depositRequiredAmount: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Percentage</span>
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={value('depositRequiredPct', '0')}
                onChange={(e) => setForm((c) => ({ ...c, depositRequiredPct: e.target.value }))}
              />
            </label>
          </div>
        )}
        {depositMode === 'rule_based' && (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={boolValue('depositRulePartsOrder', false)}
                onChange={(e) => setForm((c) => ({ ...c, depositRulePartsOrder: e.target.checked }))}
              />
              Require when parts must be ordered
            </label>
            <label className="block">
              <span className="text-sm font-medium">Require when total exceeds</span>
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={value('depositRuleMinAmount', '0')}
                onChange={(e) => setForm((c) => ({ ...c, depositRuleMinAmount: e.target.value }))}
              />
            </label>
          </div>
        )}
        <label className="mt-4 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
          <span>Deposit refundable on cancellation</span>
          <input
            type="checkbox"
            checked={boolValue('depositRefundable', true)}
            onChange={(e) => setForm((c) => ({ ...c, depositRefundable: e.target.checked }))}
          />
        </label>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Cash Discount Program</h2>
        <p className="mt-1 text-sm text-slate-500">
          Charge card customers a small fee (or discount cash) to offset processing costs.
        </p>
        <label className="mt-4 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
          <span>Enable cash discount program</span>
          <input
            type="checkbox"
            checked={cashDiscountEnabled}
            onChange={(e) => setForm((c) => ({ ...c, cashDiscountEnabled: e.target.checked }))}
          />
        </label>

        {cashDiscountEnabled ? (
          <div className="mt-4 space-y-4">
            <label className="block max-w-xs">
              <span className="text-sm font-medium text-slate-700">Program rate (%)</span>
              <input
                type="number"
                min={0}
                max={100}
                step="0.01"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={cashDiscountRatePct()}
                onChange={(e) => setForm((c) => ({ ...c, cashDiscountRate: e.target.value }))}
              />
              <span className="mt-1 block text-xs text-slate-500">e.g. 3.5 is common</span>
            </label>

            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-slate-700">How your prices work</legend>
              <label className="flex gap-3 rounded-xl border border-slate-200 p-3 text-sm">
                <input
                  type="radio"
                  name="cashDiscountMode"
                  checked={cashDiscountMode === 'card_surcharge'}
                  onChange={() => setForm((c) => ({ ...c, cashDiscountMode: 'card_surcharge' }))}
                />
                <span>
                  <span className="font-semibold">My prices are CASH prices</span>
                  <span className="mt-0.5 block text-slate-500">
                    Card customers pay {cashDiscountRatePct()}% more (e.g. $100 cash →{' '}
                    {formatCurrency(100 * (1 + numericValue(cashDiscountRatePct(), 3.5) / 100))}{' '}
                    card)
                  </span>
                </span>
              </label>
              <label className="flex gap-3 rounded-xl border border-slate-200 p-3 text-sm">
                <input
                  type="radio"
                  name="cashDiscountMode"
                  checked={cashDiscountMode === 'cash_discount'}
                  onChange={() => setForm((c) => ({ ...c, cashDiscountMode: 'cash_discount' }))}
                />
                <span>
                  <span className="font-semibold">My prices are CARD prices</span>
                  <span className="mt-0.5 block text-slate-500">
                    Cash customers pay {cashDiscountRatePct()}% less (e.g.{' '}
                    {formatCurrency(100 * (1 + numericValue(cashDiscountRatePct(), 3.5) / 100))}{' '}
                    card → $100.00 cash)
                  </span>
                </span>
              </label>
            </fieldset>

            <label className="block max-w-md">
              <span className="text-sm font-medium text-slate-700">Receipt label</span>
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={String(cashDiscountLabel)}
                onChange={(e) => setForm((c) => ({ ...c, cashDiscountLabel: e.target.value }))}
                placeholder="Cash Discount"
              />
              <span className="mt-1 block text-xs text-slate-500">
                This appears on the receipt line
              </span>
            </label>

            <label className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
              <span>Show on receipt</span>
              <input
                type="checkbox"
                checked={boolValue('cashDiscountShowOnReceipt', true)}
                onChange={(e) =>
                  setForm((c) => ({ ...c, cashDiscountShowOnReceipt: e.target.checked }))
                }
              />
            </label>

            <label className="flex items-start justify-between gap-4 rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
              <span>
                Terminal applies surcharge itself
                <span className="mt-0.5 block text-xs font-normal text-slate-500">
                  On = staff enter cash price into the reader (device adds fee). Off = enter the full
                  card price.
                </span>
              </span>
              <input
                type="checkbox"
                checked={boolValue('terminalAppliesSurcharge', true)}
                onChange={(e) =>
                  setForm((c) => ({ ...c, terminalAppliesSurcharge: e.target.checked }))
                }
              />
            </label>

            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 font-mono text-sm text-slate-700">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                Preview
              </p>
              {cashDiscountMode === 'cash_discount' ? (
                <>
                  <div className="flex justify-between">
                    <span>Screen Replacement</span>
                    <span>{formatCurrency(249.99)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-700">
                    <span>
                      {preview.discountLabel} ({(preview.discountRate * 100).toFixed(1)}%)
                    </span>
                    <span>-{formatCurrency(preview.discountAmount)}</span>
                  </div>
                  <div className="mt-2 flex justify-between border-t border-slate-300 pt-2 font-bold">
                    <span>CASH TOTAL</span>
                    <span>{formatCurrency(preview.cashTotal)}</span>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">OR for card:</p>
                  <div className="flex justify-between font-bold">
                    <span>CARD TOTAL</span>
                    <span>{formatCurrency(preview.cardTotal)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span>Screen Replacement</span>
                    <span>{formatCurrency(249.99)}</span>
                  </div>
                  <div className="mt-2 flex justify-between border-t border-slate-300 pt-2 font-bold">
                    <span>CASH TOTAL</span>
                    <span>{formatCurrency(preview.cashTotal)}</span>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">OR for card:</p>
                  <div className="flex justify-between">
                    <span>
                      {preview.discountLabel} ({(preview.discountRate * 100).toFixed(1)}%)
                    </span>
                    <span>+{formatCurrency(preview.discountAmount)}</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span>CARD TOTAL</span>
                    <span>{formatCurrency(preview.cardTotal)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Tips / Gratuity</h2>
        <p className="mt-1 text-sm text-slate-500">
          Tips are additive liability — never tax, never cash-discount base, never revenue.
        </p>
        <label className="mt-4 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
          <span>Enable tips</span>
          <input
            type="checkbox"
            checked={boolValue('tipsEnabled', false)}
            onChange={(e) => setForm((c) => ({ ...c, tipsEnabled: e.target.checked }))}
          />
        </label>
        {boolValue('tipsEnabled', false) ? (
          <div className="mt-4 space-y-3">
            <label className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
              <span>Tips on card</span>
              <input
                type="checkbox"
                checked={boolValue('tipsOnCard', true)}
                onChange={(e) => setForm((c) => ({ ...c, tipsOnCard: e.target.checked }))}
              />
            </label>
            <label className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
              <span>Tips on cash</span>
              <input
                type="checkbox"
                checked={boolValue('tipsOnCash', false)}
                onChange={(e) => setForm((c) => ({ ...c, tipsOnCash: e.target.checked }))}
              />
            </label>
            <label className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm font-medium">
              <span>Require staff PIN for tip entry</span>
              <input
                type="checkbox"
                checked={boolValue('tipsRequirePin', true)}
                onChange={(e) => setForm((c) => ({ ...c, tipsRequirePin: e.target.checked }))}
              />
            </label>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-slate-700">Attribution</legend>
              {(
                [
                  ['checkout_staff', 'Checkout staff'],
                  ['assigned_tech', 'Assigned technician'],
                  ['split', 'Split (config below)'],
                ] as const
              ).map(([mode, label]) => (
                <label key={mode} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="tipAttribution"
                    checked={
                      (form.tipAttribution as string) === mode ||
                      (!form.tipAttribution && (store?.tipAttribution ?? 'checkout_staff') === mode)
                    }
                    onChange={() => setForm((c) => ({ ...c, tipAttribution: mode }))}
                  />
                  {label}
                </label>
              ))}
            </fieldset>
            {(form.tipAttribution === 'split' ||
              (!form.tipAttribution && store?.tipAttribution === 'split')) && (
              <div className="grid max-w-sm grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="font-medium">Checkout %</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="mt-1 w-full rounded-xl border px-3 py-2"
                    value={value(
                      'tipSplitCheckout',
                      String(store?.tipSplitConfig?.checkout ?? 50),
                    )}
                    onChange={(e) => setForm((c) => ({ ...c, tipSplitCheckout: e.target.value }))}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium">Tech %</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="mt-1 w-full rounded-xl border px-3 py-2"
                    value={value('tipSplitTech', String(store?.tipSplitConfig?.tech ?? 50))}
                    onChange={(e) => setForm((c) => ({ ...c, tipSplitTech: e.target.value }))}
                  />
                </label>
              </div>
            )}
          </div>
        ) : null}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Revenue Goals</h2>
        <p className="mt-1 text-sm text-slate-500">Used for dashboard progress bars.</p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {(
            [
              ['dailyRevenueGoal', 'Daily goal ($)'],
              ['weeklyRevenueGoal', 'Weekly goal ($)'],
              ['monthlyRevenueGoal', 'Monthly goal ($)'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="block">
              <span className="text-sm font-medium text-slate-700">{label}</span>
              <input
                type="number"
                min={0}
                step="0.01"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={value(key, store?.[key] != null ? String(store[key]) : '')}
                onChange={(e) => setForm((c) => ({ ...c, [key]: e.target.value }))}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Card Payments</h2>
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
          <p>Card payments are recorded manually.</p>
          <p className="mt-2">
            Enter the amount and transaction ID from your card terminal or reader.
          </p>
          <p className="mt-3 text-slate-500">
            Coming soon: Integrated terminal processing with card reader support.
          </p>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Labor Rate</h2>
        <p className="mt-1 text-sm text-slate-500">Used only for internal P&amp;L estimates.</p>
        <label className="mt-4 block max-w-xs">
          <span className="text-sm font-medium text-slate-700">Average hourly labor rate ($)</span>
          <input
            type="number"
            min={0}
            step="0.01"
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={value(
              'avgLaborRateGoal',
              store?.avgLaborRateGoal != null ? String(store.avgLaborRateGoal) : '40',
            )}
            onChange={(e) => setForm((c) => ({ ...c, avgLaborRateGoal: e.target.value }))}
          />
        </label>
      </section>

      <button
        type="submit"
        disabled={mutation.isPending}
        className="rounded-xl bg-primary px-6 py-2.5 font-semibold text-white disabled:opacity-50"
      >
        {mutation.isPending ? 'Saving…' : 'Save Payment Settings'}
      </button>
    </form>
  );
}
